import { Clamp } from '@nova/utils';
import { IMediaInstance, PlayOptions, Sound, sound } from '@pixi/sound';

import { AudioManager } from './AudioManager';
import { IAudioManager } from './IAudioManager';

/**
 * BGM
 *
 * Handles the currently playing Background music.
 * There can only be one background music track playing at a time.
 */
export class BGM implements IAudioManager {
  private _currentTrack?: Sound;
  private _trackVolume = 0;
  /** Live mix relative to the saved music volume. 1 is normal. Not persisted. */
  private _playbackLevel = 1;

  public isMuted(): boolean {
    return AudioManager.audioSettings?.music?.mute ?? false;
  }

  public get volume(): number {
    return AudioManager.audioSettings?.music?.volume ?? 0.5;
  }

  public set volume(volume: number) {
    if (AudioManager.audioSettings?.music) {
      AudioManager.audioSettings.music.volume = Clamp(volume, 0, 1);
      this.applyLiveVolume();
      AudioManager.saveSettings();
    }
  }

  public constructor() {
    this._trackVolume = 0;

    // Reset mute state when the game is visible again or in focus.
    document.addEventListener('visibilitychange', this.onVisibilitychange.bind(this));
    globalThis.addEventListener('focus', this.onFocus.bind(this));
  }

  private onVisibilitychange(): void {
    if (document.visibilityState === 'visible') {
      if (AudioManager.audioSettings?.music?.mute) {
        this.mute();
      }
      else {
        this.unmute();
      }
    }
  }

  private onFocus(): void {
    if (!AudioManager.audioSettings?.music?.mute) {
      this.unmute();
    }
  }

  public mute(): void {
    if (this._currentTrack) {
      this._currentTrack.muted = true;
    }

    if (AudioManager.audioSettings?.music) {
      AudioManager.audioSettings.music.mute = true;
      AudioManager.saveSettings();
    }
  }

  public unmute(): void {
    if (this._currentTrack) {
      this._currentTrack.muted = false;
    }

    if (AudioManager.audioSettings?.music) {
      AudioManager.audioSettings.music.mute = false;
      AudioManager.saveSettings();
    }
  }

  /**
   * Plays the given track as the current background music.
   * If a different track was playing it will be stopped and replaced with the new track.
   * If the track as not already been loaded then this function will do nothing.
   *
   * @param track - BGM track to play
   * @param options - Options for the BGM track; by default the track will loop and be of a single instance.
   */
  public play(track: string, options: PlayOptions = { loop: true, singleInstance: true }): void {
    const trackToPlay: Sound = sound.find(track);
    if (trackToPlay && trackToPlay !== this._currentTrack) {
      this.stop();
      this._currentTrack = trackToPlay;
      this._trackVolume = options.volume || 1;
      const volume: number = this._trackVolume * this.volume * this._playbackLevel;
      const muted: boolean = AudioManager.audioSettings?.music?.mute ?? false;
      // Set the sound-level mute flag explicitly. The per-play `muted` option only silences that one
      // instance, so a later unmute() (which toggles the sound-level flag) would never restore it.
      this._currentTrack.muted = muted;
      this._currentTrack.play({ ...options, volume }) as IMediaInstance;
    }
  }

  /**
   * Scales the live track relative to the saved music volume.
   * 1 is normal, 0 is silent. Does not write the player's saved setting.
   */
  public setLevel(level: number): void {
    this._playbackLevel = Clamp(level, 0, 1);
    this.applyLiveVolume();
  }

  /** Adjust playback rate on the live track (1 = normal). No-op when nothing is playing. */
  public setSpeed(speed: number): void {
    if (this._currentTrack) {
      this._currentTrack.speed = Clamp(speed, 0.01, 4);
    }
  }

  public stop(): void {
    this._currentTrack?.stop();
  }

  public pause(): void {
    this._currentTrack?.pause();
  }

  public resume(): void {
    this._currentTrack?.resume();
  }

  private applyLiveVolume(): void {
    const instance = this._currentTrack?.instances[0];
    if (instance) {
      instance.volume = this._trackVolume * this.volume * this._playbackLevel;
    }
  }

  /**
   * Destroys the BGM manager and cleans up resources.
   */
  public destroy(): void {
    // Destroy current track
    this._currentTrack?.destroy();

    // Clear references
    this._currentTrack = undefined;
    this._trackVolume = 0;
    this._playbackLevel = 1;

    // Remove event listeners
    document.removeEventListener('visibilitychange', this.onVisibilitychange.bind(this));
    globalThis.removeEventListener('focus', this.onFocus.bind(this));
  }
}
